import type {
  EditGraph,
  EditOp,
  ResolvedEdit,
  SfxInstance,
  StickerInstance,
} from "../contracts/edit-graph.js";
import { EDIT_GRAPH_SCHEMA_VERSION } from "../contracts/edit-graph.js";
import type { BeatId, CastSlotId } from "../contracts/ids.js";
import type { SourcePack } from "../contracts/source-pack.js";

export function createEditGraph(
  pack: SourcePack,
  seed: number,
  initialOps: EditOp[] = [],
): EditGraph {
  return {
    schemaVersion: EDIT_GRAPH_SCHEMA_VERSION,
    sourcePackId: pack.id,
    sourcePackVersion: pack.version,
    tickRate: pack.tickRate,
    seed,
    ops: [...initialOps],
    cursor: initialOps.length,
  };
}

/**
 * Pure reducer: append op (truncating redo), or move cursor for undo/redo.
 * Does not mutate the input graph.
 */
export type EditCommand =
  | { type: "apply"; op: EditOp }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; seed?: number };

export function reduceEditGraph(graph: EditGraph, command: EditCommand): EditGraph {
  switch (command.type) {
    case "apply": {
      const ops = graph.ops.slice(0, graph.cursor);
      ops.push(command.op);
      return { ...graph, ops, cursor: ops.length };
    }
    case "undo":
      return { ...graph, cursor: Math.max(0, graph.cursor - 1) };
    case "redo":
      return { ...graph, cursor: Math.min(graph.ops.length, graph.cursor + 1) };
    case "reset":
      return {
        ...graph,
        seed: command.seed ?? graph.seed,
        ops: [],
        cursor: 0,
      };
    default: {
      const _exhaustive: never = command;
      return _exhaustive;
    }
  }
}

/** Replay active ops from an empty graph with the same identity/seed. */
export function replayEditGraph(graph: EditGraph): EditGraph {
  let next: EditGraph = {
    schemaVersion: graph.schemaVersion,
    sourcePackId: graph.sourcePackId,
    sourcePackVersion: graph.sourcePackVersion,
    tickRate: graph.tickRate,
    seed: graph.seed,
    ops: [],
    cursor: 0,
  };
  for (const op of graph.ops.slice(0, graph.cursor)) {
    next = reduceEditGraph(next, { type: "apply", op });
  }
  return next;
}

/** Project active ops into a resolved view for scoring and adapters. */
export function resolveEdit(graph: EditGraph, pack: SourcePack): ResolvedEdit {
  const active = graph.ops.slice(0, graph.cursor);
  let beatOrder: BeatId[] = [...pack.canonicalRecord.beatOrder];
  const cuts = new Map<BeatId, Extract<EditOp, { kind: "cut" }>>();
  const frames = new Map<BeatId, Extract<EditOp, { kind: "frame" }>>();
  // Id-keyed caption list: last op per captionId wins, first-seen id order.
  // Re-emitting a caption (e.g. an on-video style or placement change) REPLACES
  // it rather than stacking a duplicate. Mirrors the sticker list below.
  const captionOrder: string[] = [];
  const captionById = new Map<string, Extract<EditOp, { kind: "text" }>>();
  const sounds: Extract<EditOp, { kind: "sound" }>[] = [];
  const casts = new Map<CastSlotId, Extract<EditOp, { kind: "cast" }>>();
  // Id-keyed one-shot SFX list: last op per id wins, cleared ids omitted, first-seen order.
  const sfxOrder: string[] = [];
  const seenSfxIds = new Set<string>();
  const sfxById = new Map<string, SfxInstance>();
  let look: Extract<EditOp, { kind: "look" }> | null = null;
  const motions = new Map<BeatId, Extract<EditOp, { kind: "motion" }>>();
  // Id-keyed sticker list: last op per id wins, cleared ids omitted, first-seen id order.
  const stickerOrder: string[] = [];
  const seenStickerIds = new Set<string>();
  const stickerById = new Map<string, StickerInstance>();
  const transitions = new Map<BeatId, Extract<EditOp, { kind: "transition" }>>();

  for (const op of active) {
    switch (op.kind) {
      case "cut":
        cuts.set(op.beatId, op);
        break;
      case "reorder":
        beatOrder = [...op.beatOrder];
        break;
      case "frame":
        frames.set(op.beatId, op);
        break;
      case "text":
        if (!captionById.has(op.captionId)) captionOrder.push(op.captionId);
        captionById.set(op.captionId, op);
        break;
      case "sound":
        // Last-wins: one effective treatment under the cursor (frames/casts pattern).
        sounds.length = 0;
        sounds.push(op);
        break;
      case "cast":
        casts.set(op.slotId, op);
        break;
      case "sfx":
        if (!seenSfxIds.has(op.id)) {
          seenSfxIds.add(op.id);
          sfxOrder.push(op.id);
        }
        if (op.assetId === null) {
          sfxById.delete(op.id);
        } else {
          sfxById.set(op.id, {
            id: op.id,
            assetId: op.assetId,
            atTick: op.atTick,
            volumeMilli: op.volumeMilli,
          });
        }
        break;
      case "look":
        look = op.treatmentId === null ? null : op;
        break;
      case "motion":
        if (op.presetId === null) {
          motions.delete(op.beatId);
        } else {
          motions.set(op.beatId, op);
        }
        break;
      case "sticker":
        if (!seenStickerIds.has(op.id)) {
          seenStickerIds.add(op.id);
          stickerOrder.push(op.id);
        }
        if (op.stickerId === null) {
          stickerById.delete(op.id);
        } else {
          stickerById.set(op.id, {
            id: op.id,
            stickerId: op.stickerId,
            anchorXMilli: op.anchorXMilli,
            anchorYMilli: op.anchorYMilli,
            scaleMilli: op.scaleMilli,
            rotationMilli: op.rotationMilli,
            startTick: op.startTick,
            endTick: op.endTick,
          });
        }
        break;
      case "transition":
        if (op.presetId === null) {
          transitions.delete(op.beatId);
        } else {
          transitions.set(op.beatId, op);
        }
        break;
    }
  }

  // Default keep for beats without an explicit cut.
  for (const beatId of beatOrder) {
    if (!cuts.has(beatId)) {
      cuts.set(beatId, {
        kind: "cut",
        opId: `default-keep:${beatId}`,
        beatId,
        mode: "keep",
      });
    }
  }

  // Transitions only apply at outgoing boundaries of retained beats
  // (non-omit, non-empty-trim), never after the final retained beat.
  const retainedBeatOrder = beatOrder.filter((id) => {
    const cut = cuts.get(id);
    if (!cut || cut.mode === "keep") return true;
    if (cut.mode === "omit") return false;
    if (cut.mode === "trim") {
      if (cut.inTick === undefined || cut.outTick === undefined) return false;
      return cut.outTick > cut.inTick;
    }
    return true;
  });
  const eligibleTransitionBeats = new Set(retainedBeatOrder.slice(0, -1));
  for (const beatId of [...transitions.keys()]) {
    if (!eligibleTransitionBeats.has(beatId)) {
      transitions.delete(beatId);
    }
  }

  // First-seen captionId order, last op per id.
  const captions = captionOrder
    .map((id) => captionById.get(id))
    .filter((c): c is Extract<EditOp, { kind: "text" }> => c !== undefined);

  // First-seen id order, minus cleared ids.
  const stickers: StickerInstance[] = stickerOrder
    .map((id) => stickerById.get(id))
    .filter((s): s is StickerInstance => s !== undefined);

  // First-seen id order, minus cleared ids.
  const sfx: SfxInstance[] = sfxOrder
    .map((id) => sfxById.get(id))
    .filter((s): s is SfxInstance => s !== undefined);

  return {
    beatOrder,
    cuts,
    frames,
    captions,
    sounds,
    casts,
    sfx,
    look,
    motions,
    stickers,
    transitions,
  };
}

export function activeOps(graph: EditGraph): EditOp[] {
  return graph.ops.slice(0, graph.cursor);
}
